import { Fragment, useState } from "react";
import { useInView } from "react-intersection-observer";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import {
  getSectionNavigationPath,
  useArticleHeaderState,
} from "@components/Header/helpers";
import EshopOverlayVisibilityGate from "@features/onsiteEshop/EshopOverlayVisibilityGate";
import { useGetPosthogVariant } from "@features/posthog/hooks/useGetPosthogVariant";
import {
  FEATURE_FLAG_DEEPERDIVE_AB_DEMO,
  FEATURE_FLAG_DEEPERDIVE_AB_TEST,
} from "@features/posthog/utils/constants";
import useBypassDeeperdive from "@features/taboola/hooks/useBypassDeeperdive";
import useIsTaboolaEnabled from "@features/taboola/hooks/useIsTaboolaEnabled";
import useArticleGiftReceived from "@hooks/useArticleGiftReceived";
import useGenerateGiftURL from "@hooks/useGenerateGiftURL";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import ArticleDivider from "@routes/Article/components/ArticleDivider";
import ArticleSubshareModal from "@routes/Article/components/ArticleSubshare/ArticleSubshareModal";
import ArticleSubsharePrompt from "@routes/Article/components/ArticleSubshare/ArticleSubsharePrompt";
import ArticleSubshareReceived from "@routes/Article/components/ArticleSubshare/ArticleSubshareReceived";
import LayoutFactory from "@routes/Article/components/Layout/LayoutFactory";
import { useNextArticle } from "@routes/Article/hooks/useNextArticle";
import { LAMBDA_RESPONSE_TYPES } from "@routes/Article/types/Alacrity";
import {
  getUrlPathFromAbsoluteUrl,
  VerticalHeaderFactory,
} from "@routes/Article/utils/helpers";
import {
  ArticlePageData,
  FeedArticleData,
  FeedArticleItems,
} from "@routes/Article/utils/types";
import { ArticleDisplayType } from "@typings/enums";

const ArticlePageContent = ({
  articleData,
  seoFooterData,
  isChinese,
  isPreview,
  isSummaryAvailable,
  trendingCards,
}: ArticlePageData): React.ReactElement => {
  const { isBypassEnabledReady, isBypassEnabled } = useBypassDeeperdive();
  const { gaVariant } = useGetPosthogVariant({
    flag: FEATURE_FLAG_DEEPERDIVE_AB_TEST,
    isEnabledFeatureFlagVariant: isBypassEnabledReady && !isBypassEnabled,
  });

  const data = {
    ...articleData,
    isSummaryAvailable,
  };
  const { sections, tags, paidMode, sentiment } = articleData;

  const [showGiftSubscribePrompt, setShowGiftSubscribePrompt] = useState(false);
  const [articleInView, setArticleInView] = useState<FeedArticleData>(data);

  const { isModalOpen, setIsModalOpen, alacrityRes, isGiftReceived } =
    useArticleGiftReceived({ paidMode });

  const isTaboolaEnabled = useIsTaboolaEnabled();

  const _gaVariant =
    isBypassEnabledReady && isBypassEnabled
      ? FEATURE_FLAG_DEEPERDIVE_AB_DEMO
      : gaVariant;

  const { articleTopRef, isShowArticleHeader } = useArticleHeaderState();

  // The main section of the article.
  const mainSection = articleInView.sections?.[0] || sections?.[0];
  const mainSectionURLPath = getUrlPathFromAbsoluteUrl(mainSection.urlPath);

  // The parent section if main section has parent, otherwise this is also the main section.
  const parentSection = mainSection?.parent || mainSection;
  const parentSectionUrlPath =
    getUrlPathFromAbsoluteUrl(parentSection?.urlPath) || mainSectionURLPath;

  const verticalPath = mainSectionURLPath || "";
  const isThisArticlegifted =
    alacrityRes?.data.responseType ===
      LAMBDA_RESPONSE_TYPES.SUCCESS_VALID_GIFT_TOKEN &&
    !alacrityRes.data.viewGiftStatus;

  const VerticalHeaderLayout = VerticalHeaderFactory({ verticalPath });

  const { ref: showGiftSubscribePromptRef } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (inView && isThisArticlegifted) setShowGiftSubscribePrompt(true);
    },
  });

  const handleSetArticle = (e: FeedArticleItems) => {
    setArticleInView(e);
  };

  const sectionPath = getSectionNavigationPath(
    tags || [],
    parentSectionUrlPath
  );

  const isPremium = paidMode === "premium";

  const {
    alacrityRes: giftURlAlacrityRes,
    isModalOpen: giftURlisModalOpen,
    setIsModalOpen: setGiftURLIsModalOpen,
    handleGenerateGiftURLModal: handleGenerateGiftURLModal,
  } = useGenerateGiftURL(isPremium);

  const articleInViewGiftRes = giftURlAlacrityRes.find(
    (res) => res.pathname === getUrlPathFromAbsoluteUrl(articleInView.urlPath)
  );

  const { moreStories, isLoading, ref } = useNextArticle({
    data,
    lang: isChinese ? "zh-hans" : "en",
  });

  const firstArticleUrlPath =
    getUrlPathFromAbsoluteUrl(moreStories[0]?.urlPath) || "";
  const { gsChannels } = useGrapeshotData({
    urlPath: firstArticleUrlPath,
    index: -1,
    keywords: sentiment?.keywords,
  });

  return (
    <div className="w-full" data-testid="article-content-component">
      <Ad
        adType="lb1"
        className="no-print flex min-h-[50px] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 md:py-3 lg:min-h-[250px]"
      />

      <Header
        article={articleInView}
        displayArticle={isShowArticleHeader}
        parentCategory={sectionPath}
        handleGenerateGiftURLModal={handleGenerateGiftURLModal}
        setIsModalOpen={setGiftURLIsModalOpen}
        alacrityRes={articleInViewGiftRes?.alacrityRes}
        isPremium={isPremium}
      />

      {VerticalHeaderLayout ? (
        <Container rootClassName="md:px-20 pt-8">
          <VerticalHeaderLayout className="w-full md:w-10/12 lg:w-8/12" />
        </Container>
      ) : null}

      <div
        id="article-content-wrapper"
        className="relative w-full text-gray-850"
      >
        {moreStories.map((article, index) => {
          // Check if article is undefined
          if (!article) return null;

          const displayType =
            article?.displayType || ArticleDisplayType.MainNewsDefault;

          const ArticleLayout = LayoutFactory({
            displayType,
            path: getUrlPathFromAbsoluteUrl(article.urlPath),
          });

          if (!ArticleLayout) return null;

          return (
            <Fragment key={article.id}>
              {index > 0 ? <ArticleDivider /> : null}

              <ArticleLayout
                article={article}
                index={index}
                gsChannels={gsChannels}
                handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                setIsModalOpen={setGiftURLIsModalOpen}
                alacrityRes={articleInViewGiftRes?.alacrityRes}
                isPremium={isPremium}
                isModalOpen={giftURlisModalOpen}
                setArticleObj={handleSetArticle}
                articleInView={articleInView}
                displayArticleRef={articleTopRef}
                isGifted={isGiftReceived}
                isPreview={isPreview}
                isNavigate={moreStories.length > 1}
                isChinese={isChinese}
                isSummaryAvailable={article.isSummaryAvailable}
                isTaboolaEnabled={isTaboolaEnabled}
                trendingCards={trendingCards}
                gaVariant={_gaVariant}
              />

              <div ref={ref} />
            </Fragment>
          );
        })}
      </div>

      {isLoading ? <ArticleDivider isLoading={isLoading} /> : null}

      <ArticleSubsharePrompt
        isModalOpen={showGiftSubscribePrompt}
        setIsModalOpen={setShowGiftSubscribePrompt}
      />

      <div
        aria-roledescription="article-subshare-prompt-observer"
        ref={showGiftSubscribePromptRef}
      ></div>

      <SeoFooter data={seoFooterData} />

      <EshopOverlayVisibilityGate componentId="catfish">
        <Ad adType="catfish" />
      </EshopOverlayVisibilityGate>

      <Ad adType="prestitial" />

      <>
        {alacrityRes ? (
          <ArticleSubshareModal
            isModalOpen={isModalOpen && !alacrityRes.data.viewGiftStatus}
            setIsModalOpen={setIsModalOpen}
          >
            <ArticleSubshareReceived
              alacrityRes={alacrityRes}
              setIsModalOpen={setIsModalOpen}
            />
          </ArticleSubshareModal>
        ) : null}
      </>
    </div>
  );
};

export default ArticlePageContent;
